import clsx from "clsx";
import { FC, MouseEvent, Ref, useRef } from "react";

import { COURSE_ROLE_TO_ICON, ExtendedOrgRoleType, ORG_ROLE_TO_ICON } from "./UserRoleIcons";

import { InviteStatus } from "api/api-data-types";
import { serverRequestResetPassword } from "api/api-server-actions";
import { UserEnrollmentSubMenu } from "components/menus/UserEnrollmentSubMenu";
import { CourseRoleType, CourseUserType, UserType } from "components/server-types";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { LegacyButton } from "mds/components/LegacyButton";
import { Menu, MenuInstance, MenuItem, SubMenu } from "mds/components/Menu";
import { Tooltip } from "mds/components/Tooltip";
import {
  CircleDisabledIcon,
  CircleInfoIcon,
  CopySendArrowIcon,
  DotMenuVerticalIcon,
  LongFormPencilIcon,
  PencilEditIcon,
  PersonSingleIcon,
  TextBooksIcon,
} from "mds/icons";
import { useAppDispatch, useAppSelector } from "store/index";
import { selectIsOrgUserDisabled } from "store/selectors";
import { computedStateActions } from "store/slices/computed";
import { toastErrorMessage, toastSuccessMessage } from "utils/alerts";
import { COURSE_ROLES, getCourseResourceRole } from "utils/course";

const ORG_ROLES = ["admin", "other"] as ("admin" | "other")[];

export type AdminUserOptionsMenuProps = {
  user: UserType & { is_disabled?: boolean; invite_status: InviteStatus };
  currentRole: ExtendedOrgRoleType;
  onChangeRole?: (newRole: "admin" | "other") => void;
  onImpersonate?: () => void;
  onToggleDisabled?: () => void;
  onEdit?: () => void;
};

export const AdminUserOptionsMenu: FC<AdminUserOptionsMenuProps> = ({
  user,
  currentRole,
  onChangeRole,
  onImpersonate,
  onToggleDisabled,
  onEdit,
}) => {
  const menuRef: Ref<MenuInstance | null> = useRef(null);
  const dispatch = useAppDispatch();

  const { email, id: userId, invite_status, is_disabled } = user;

  const handleSendInvite = async () => {
    try {
      // We send the invite instead of the password reset if the user has not accepted the eula
      await serverRequestResetPassword(email);

      // Update the user's invite status in the computed slice to reflect the pending state
      dispatch(
        computedStateActions.saveUserInviteStatuses({
          [userId]: InviteStatus.PENDING,
        }),
      );

      toastSuccessMessage(t("user_options_menu.send_invite_success"));
    } catch (error) {
      console.error("Failed to send invite:", error);
      toastErrorMessage(t("user_options_menu.send_invite_error"));
    }
  };

  const MenuButton = (
    <LegacyButton
      kind="tertiary"
      size="s"
      title={t("common.options")}
      onClick={(e: MouseEvent) => {
        e.stopPropagation();
      }}
    >
      <DotMenuVerticalIcon />
    </LegacyButton>
  );

  return (
    <Menu
      direction="bottom"
      instanceRef={menuRef}
      limitItemHeight={false}
      menuButton={MenuButton}
      menuClassName="min-w-[180px]"
      onClick={(e) => e.stopPropagation()}
    >
      {email && (
        <>
          <MenuItem
            className="h-auto"
            eventName="Org User Options - Send email"
            eventProps={{ userEmail: email, userId }}
            href={`mailto:${email}`}
            target="_blank"
            allowPropagation
          >
            <div className="flex flex-col gap-1">
              <IconText
                iconStart={<LongFormPencilIcon />}
                text={t("user_options_menu.email_user")}
              />
              <div className="ml-6 block">{email}</div>
            </div>
          </MenuItem>

          {invite_status !== InviteStatus.ACCEPTED && !is_disabled && (
            <MenuItem
              className="h-auto"
              eventName="Org User Options - Send invite"
              eventProps={{ userEmail: email, userId }}
              onClick={handleSendInvite}
            >
              <div className="flex flex-col gap-1">
                <IconText
                  iconStart={<CopySendArrowIcon />}
                  text={
                    invite_status === InviteStatus.NOT_SENT
                      ? t("user_options_menu.send_invite")
                      : t("user_options_menu.resend_invite")
                  }
                />
                <div className="ml-6 block">{email}</div>
              </div>
            </MenuItem>
          )}
        </>
      )}

      {onImpersonate && (
        <MenuItem
          eventName="Org User Options - Impersonate user"
          eventProps={{ userEmail: email, userId }}
          onClick={onImpersonate}
        >
          <IconText
            iconStart={<PersonSingleIcon />}
            text={t("user_options_menu.impersonate_user")}
          />
        </MenuItem>
      )}

      <SubMenu
        label={
          <IconText
            iconStart={<TextBooksIcon />}
            text={t("user_options_menu.update_enrolled_courses.menu_label")}
          />
        }
        allowPropagation
        withArrow
      >
        <UserEnrollmentSubMenu closeMenu={() => menuRef.current?.closeMenu()} userId={userId} />
      </SubMenu>

      <MenuItem
        className="justify-between"
        disabled={user.is_superuser}
        eventName="Org User Options - Edit user information"
        eventProps={{ userEmail: email, userId }}
        onClick={onEdit}
      >
        <IconText
          iconStart={<PencilEditIcon />}
          text={t("user_options_menu.edit_user_info.menu_label")}
        />
        {user.is_superuser && (
          <Tooltip content={t("user_options_menu.edit_user_info.superuser_disabled")}>
            <CircleInfoIcon />
          </Tooltip>
        )}
      </MenuItem>

      {onChangeRole && currentRole && currentRole !== "none" && (
        <SubMenu
          disabled={user.is_superuser}
          label={
            <div className="flex w-full items-center justify-between gap-1">
              <IconText
                iconStart={ORG_ROLE_TO_ICON[currentRole]}
                text={t("user_options_menu.edit_role")}
              />
              {user.is_superuser && (
                <Tooltip content={t("user_options_menu.edit_user_info.superuser_disabled")}>
                  <CircleInfoIcon />
                </Tooltip>
              )}
            </div>
          }
          position="auto"
          withArrow={!user.is_superuser}
        >
          {ORG_ROLES.map((role) => (
            <MenuItem
              className={clsx({ selected: currentRole === role })}
              eventName="Org User Options - Change user role"
              eventProps={{ userEmail: email, userId }}
              key={role}
              onClick={() => onChangeRole(role)}
            >
              <IconText iconStart={ORG_ROLE_TO_ICON[role]} text={t(`roles.org_${role}`)} />
            </MenuItem>
          ))}
        </SubMenu>
      )}

      {onToggleDisabled &&
        (is_disabled ? (
          <MenuItem
            disabled={!onToggleDisabled}
            eventName="Org User Options - Enable org user"
            eventProps={{ userEmail: email, userId }}
            onClick={onToggleDisabled}
          >
            <IconText iconStart={<PersonSingleIcon />} text={t("user_actions.enable")} />
          </MenuItem>
        ) : (
          <MenuItem
            disabled={!onToggleDisabled}
            eventName="Org User Options - Disable org user"
            eventProps={{ userEmail: email, userId }}
            onClick={onToggleDisabled}
          >
            <IconText
              iconStart={<CircleDisabledIcon className="text-red" />}
              text={t("user_actions.disable")}
            />
          </MenuItem>
        ))}
    </Menu>
  );
};

export type CourseUserOptionsMenuProps = {
  onClickDisable?: () => void;
  onChangeRole?: (newRole: CourseRoleType) => void;
  onImpersonate?: () => void;
  user: UserType;
  courseUser: CourseUserType;
};

export const CourseUserOptionsMenu: FC<CourseUserOptionsMenuProps> = ({
  onClickDisable,
  onChangeRole,
  onImpersonate,
  user,
  courseUser,
}) => {
  const isOrgUserDisabled = useAppSelector((state) => selectIsOrgUserDisabled(state, user.id));
  const { role: currentRole, is_disabled } = courseUser;
  const createdInLms = courseUser.creation_source === "lms";
  const { email } = user;

  const MenuButton = (
    <LegacyButton
      kind="tertiary"
      size="s"
      title={t("common.options")}
      onClick={(e: MouseEvent) => {
        e.stopPropagation();
      }}
    >
      <DotMenuVerticalIcon />
    </LegacyButton>
  );

  const LmsManagedTooltip = <Tooltip content={t("user_options_menu.lms_managed_user")} />;
  const OrgUserDisabledTooltip = <Tooltip content={t("user_options_menu.org_user_disabled")} />;

  return (
    <Menu
      direction="left"
      limitItemHeight={false}
      menuButton={MenuButton}
      menuClassName="min-w-[180px]"
      onClick={(e) => e.stopPropagation()}
    >
      {email && (
        <MenuItem
          className="h-auto"
          eventName="User Options - Send email"
          eventProps={{ userEmail: email, userId: user.id }}
          href={`mailto:${email}`}
          target="_blank"
          allowPropagation
        >
          <div className="flex flex-col gap-1">
            <IconText iconStart={<LongFormPencilIcon />} text={t("user_options_menu.email_user")} />
            <div className="ml-6 block">{email}</div>
          </div>
        </MenuItem>
      )}

      {onImpersonate && (
        <MenuItem
          eventName="User Options - Impersonate user"
          eventProps={{ userEmail: email, userId: user.id }}
          onClick={onImpersonate}
        >
          <IconText
            iconStart={<PersonSingleIcon />}
            text={t("user_options_menu.impersonate_user")}
          />
        </MenuItem>
      )}

      {onChangeRole && currentRole && (
        <SubMenu
          className={clsx({ disabled: createdInLms })}
          disabled={createdInLms}
          label={
            <div className="flex w-full items-center justify-between gap-1">
              <IconText
                iconStart={COURSE_ROLE_TO_ICON[currentRole]}
                text={t("user_options_menu.edit_role")}
              />
              {createdInLms && LmsManagedTooltip}
            </div>
          }
          withArrow={!createdInLms}
        >
          {COURSE_ROLES.map((role) => (
            <MenuItem
              className={clsx({ selected: currentRole === role })}
              eventName="User Options - Change user role"
              eventProps={{
                userId: user.id,
                userEmail: user.email,
                userGivenName: user.first_name,
                userFamilyName: user.last_name,
                newRole: role,
              }}
              key={role}
              onClick={() => onChangeRole(role)}
            >
              <IconText
                iconStart={COURSE_ROLE_TO_ICON[role]}
                text={t(`roles.course_${getCourseResourceRole(role)}`)}
              />
            </MenuItem>
          ))}
        </SubMenu>
      )}

      {onClickDisable && (
        <MenuItem
          className={clsx("flex w-full items-center justify-between", {
            disabled: createdInLms || isOrgUserDisabled,
          })}
          disabled={createdInLms || isOrgUserDisabled}
          eventName={is_disabled ? "User Options - Restore user" : "User Options - Disable user"}
          eventProps={{
            userId: user.id,
            userEmail: user.email,
            userGivenName: user.first_name,
            userFamilyName: user.last_name,
          }}
          kind={!is_disabled ? "destructive" : undefined}
          onClick={onClickDisable}
        >
          <IconText
            iconStart={
              !is_disabled ? <CircleDisabledIcon className="text-red" /> : <PersonSingleIcon />
            }
            text={is_disabled ? t("user_actions.enable") : t("user_actions.disable")}
          />
          {createdInLms && LmsManagedTooltip}
          {isOrgUserDisabled && OrgUserDisabledTooltip}
        </MenuItem>
      )}
    </Menu>
  );
};
